import React, { FC, useMemo, useState } from 'react';
import css from './RepInfoBox.scss';
import { DriftCircularProgress, DriftSelect, DriftSelectOption, LocalPhoneIcon, Tooltip, InfoIcon } from 'drift-ui';
import emailIcon from '../../assets/images/email-icon.svg';
import { Rep, useGetFactoringRepQuery, useGetRepsQuery } from '../../services/repRetrievalService';
import { formatPhoneNumber } from '../../utils';

interface RepInfoBoxProps {
  email: string;
}
interface RepCardProps {
  rep: Rep;
  email: string;
}

const toolTipTitle: Record<string, string> = {
  'RTS Financial': 'Factoring',
  'Ryan Transportation': 'Freight',
  'Carrier Services': 'Fuel',
  ProTransport: 'TM Software',
};

export const RepInfoBox: FC<RepInfoBoxProps> = ({ email }) => {
  const { data, isLoading } = useGetRepsQuery(email);
  return (
    <div className={css.repInfoBoxContainer}>
      {isLoading ? (
        <div className={css.loadingSpinnerWide}>
          <DriftCircularProgress size="large" variant="indeterminate" />
          <span>Loading...</span>
        </div>
      ) : (
        data?.map((rep, idx) => {
          return (
            <React.Fragment key={`${rep?.product}-${idx}`}>
              <RepCard rep={rep} email={email} />
              <div className={css.lineBorder} />
            </React.Fragment>
          );
        })
      )}
    </div>
  );
};

const RepCard: FC<RepCardProps> = ({ rep: initRep, email }) => {
  const [valueSelected, setValueSelected] = useState(initRep.activeAccount);
  const [isLoading, setIsLoading] = useState(false);
  const mappedAccounts = useMemo(
    () =>
      initRep.accounts?.map((value: string): DriftSelectOption => {
        const valueActive = value === initRep.activeAccount ? value + ' (Active)' : value;
        return { label: valueActive, value };
      }),
    [initRep.accounts],
  );

  const skip = !valueSelected || !initRep.accounts;
  const { data: [resData] = [] } = useGetFactoringRepQuery(
    { account: valueSelected as string, email, setIsLoading },
    { skip },
  );

  const rep = resData || initRep;

  return (
    <div className={css.repCardContainer}>
      <div className={css.productTitleContainer}>
        <p className={css.productTitle}>{rep.product}</p>
        <Tooltip title={toolTipTitle[rep.product]} placement="top">
          <InfoIcon />
        </Tooltip>
      </div>
      {rep.accounts && (
        <DriftSelect
          label="Account Number"
          value={valueSelected}
          onChange={(event) => setValueSelected(event.target.value as string)}
          options={mappedAccounts ?? []}
        />
      )}
      <CardBody isLoading={isLoading} rep={rep} />
    </div>
  );
};

const CardBody: FC<{ isLoading: boolean; rep: Rep }> = ({ rep, isLoading }) =>
  isLoading ? <LoadingFactoring /> : <RepData rep={rep} />;

const LoadingFactoring: FC = () => {
  return (
    <div className={css.loadingSpinner}>
      <DriftCircularProgress size="large" variant="indeterminate" />
      <span>Loading...</span>
    </div>
  );
};

const RepData: FC<{ rep: Rep }> = ({ rep }) => {
  if (rep.name) {
    const phone = formatPhoneNumber(rep.phone);
    return (
      <div>
        <div className={css.nameContainer}>
          <p className={css.repTitle}>{`Rep:`}</p>
          <p className={css.repName}>{rep.name}</p>
        </div>
        <div className={css.repSubItemContainer}>
          <div className={css.phoneContainer}>
            <LocalPhoneIcon />
            <p className={css.phoneNumber}>{phone}</p>
          </div>
          <p className={css.extContainer}>{`Ext: ${rep.ext ?? 'N/A'}`}</p>
          <div className={css.emailContainer}>
            <img alt="email-icon" src={emailIcon} />
            <p className={css.email}>{rep.email}</p>
          </div>
        </div>
        <div className={css.officeInfo}>
          <p className={css.officeInfoText}>{`Office: ${rep.office ? rep.office : 'N/A'}`}</p>
          <p className={css.officeInfoText}>{`Timezone: ${rep.timezone ? rep.timezone : 'N/A'}`}</p>
        </div>
      </div>
    );
  } else {
    return (
      <p className={css.noProduct}>
        Carrier does not <br /> have this service
      </p>
    );
  }
};
